import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { fetchCdnSigningRefresh } from '@/utils/api';
import { CdnSigningParams, getCdnSigningRefreshDelayMs } from '@/utils/cdnSigning';

/** Initial retry delay after a failed CDN signing refresh (network/4xx/5xx). */
const CDN_SIGNING_REFRESH_RETRY_BASE_MS = 30 * 1000;
/** Cap exponential backoff so retries do not stretch indefinitely between attempts. */
const CDN_SIGNING_REFRESH_RETRY_MAX_MS = 5 * 60 * 1000;
/** Stop retrying after this many consecutive failures to avoid hammering a down endpoint. */
const CDN_SIGNING_REFRESH_MAX_RETRIES = 5;

export function getCdnSigningRefreshRetryDelayMs(consecutiveFailures: number): number {
    const exponent = Math.max(0, consecutiveFailures - 1);
    return Math.min(CDN_SIGNING_REFRESH_RETRY_BASE_MS * 2 ** exponent, CDN_SIGNING_REFRESH_RETRY_MAX_MS);
}

export type CdnSigningRefreshParams = {
    articleId: number;
    locale?: string;
};

/**
 * Keeps CloudFront signing credentials fresh for long HLS sessions.
 * Updates signingRef in place so hls.js xhrSetup always uses the latest params.
 */
export function useCdnSigningRefresh(
    initialSigning: CdnSigningParams | null | undefined,
    params: CdnSigningRefreshParams | null,
    enabled: boolean
): MutableRefObject<CdnSigningParams | null | undefined> {
    const signingRef = useRef<CdnSigningParams | null | undefined>(initialSigning);
    const paramsRef = useRef(params);
    paramsRef.current = params;

    useEffect(() => {
        signingRef.current = initialSigning;
    }, [initialSigning]);

    useEffect(() => {
        if (!enabled || !params?.articleId) {
            return;
        }

        const signing = initialSigning;
        if (signing?.expiresAt == null) {
            return;
        }

        let cancelled = false;
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        let consecutiveFailures = 0;

        const performRefresh = async () => {
            if (cancelled) {
                return;
            }

            const refreshParams = paramsRef.current;
            if (!refreshParams?.articleId) {
                return;
            }

            try {
                const result = await fetchCdnSigningRefresh(refreshParams.articleId, {
                    locale: refreshParams.locale,
                });
                if (cancelled) {
                    return;
                }
                consecutiveFailures = 0;
                signingRef.current = result.cdnSigning;
                scheduleRefresh(result.cdnSigning);
            } catch (error) {
                consecutiveFailures += 1;
                console.error('CDN signing refresh failed', error);
                if (cancelled) {
                    return;
                }
                if (consecutiveFailures > CDN_SIGNING_REFRESH_MAX_RETRIES) {
                    console.error(
                        `CDN signing refresh stopped after ${CDN_SIGNING_REFRESH_MAX_RETRIES} consecutive failures`
                    );
                    return;
                }
                timeoutId = setTimeout(performRefresh, getCdnSigningRefreshRetryDelayMs(consecutiveFailures));
            }
        };

        const scheduleRefresh = (signingParams: CdnSigningParams) => {
            const delayMs = getCdnSigningRefreshDelayMs(signingParams.expiresAt);
            if (delayMs === null) {
                return;
            }

            timeoutId = setTimeout(performRefresh, delayMs);
        };

        scheduleRefresh(signing);

        return () => {
            cancelled = true;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [enabled, initialSigning, params?.articleId]);

    return signingRef;
}
